import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { useAuth } from "../context/AuthContext";
import { allowedSites } from "../lib/sites";
import { SCHEMAS } from "../lib/permitSchemas";
import { SchemaPrint } from "../components/SchemaPrint";
import { SchemaForm } from "../components/SchemaForm";
import { SignaturePad } from "../components/SignaturePad";
import { ContractorInvitePanel } from "../components/ContractorInvitePanel";
import { SiteInductionQRGrid } from "../components/QRTiles";

const SITE_INFO = { ...SCHEMAS.site_induction, sections: SCHEMAS.site_induction.sections.filter((s) => s.id === "site_info" || s.id === "brief") };
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Loader2, ClipboardCheck, MapPin, UserCheck, X } from "lucide-react";

export default function InductionRequests() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [review, setReview] = useState(null);
  const [edit, setEdit] = useState({});
  const [siteData, setSiteData] = useState({});
  const [inductorName, setInductorName] = useState("");
  const [inductorSig, setInductorSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const sites = allowedSites(user);

  useEffect(() => {
    if (review) {
      setEdit({
        first_name: review.first_name || "", last_name: review.last_name || "", company: review.company || "",
        email: review.email || "", dbs_number: review.dbs_number || "", mobile: review.mobile || "",
        emergency_contact: review.emergency_contact || "", notes: review.notes || "", site: review.site || "",
      });
      setSiteData(review.induction_data || {});
      setInductorName(user?.name || "");
      setInductorSig(null);
    }
  }, [review, user]);

  const setE = (k, v) => setEdit((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/induction-submissions").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const approve = async () => {
    setBusy(true);
    try {
      if (!inductorName.trim()) { setBusy(false); return toast.error("Please enter the inductor's name."); }
      if (!inductorSig) { setBusy(false); return toast.error("Please add the inductor's signature."); }
      await api.post(`/induction-submissions/${review.id}/approve`, { ...edit, induction_data: siteData, inductor_name: inductorName, inductor_signature: inductorSig });
      toast.success("Induction approved — contractor created");
      setReview(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const reject = async (item) => {
    try { await api.post(`/induction-submissions/${item.id}/reject`); toast.success("Submission rejected"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <AppShell>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">GFM - Permit to Work Management System</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Requests & Invites</h1>
          <p className="text-sm text-muted-foreground">Review and approve inductions contractors submit via the QR code.</p>
        </div>
      </div>

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-border bg-white px-4 py-12 text-center text-muted-foreground shadow-sm" data-testid="no-requests">
          No induction requests waiting for review.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-white shadow-sm" data-testid="requests-table">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Site</th><th className="px-4 py-3">Submitted</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-border" data-testid={`request-row-${it.id}`}>
                  <td className="px-4 py-3 font-medium">{it.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{it.company}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-brand"><MapPin className="h-3 w-3" />{it.site}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{it.submitted_at ? new Date(it.submitted_at).toLocaleString("en-GB") : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" className="bg-brand hover:bg-brand-dark" onClick={() => setReview(it)} data-testid={`review-btn-${it.id}`}>Review</Button>
                      <Button size="sm" variant="outline" onClick={() => reject(it)} data-testid={`reject-request-${it.id}`}><X className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-10" data-testid="qr-invites-section">
        <h2 className="mb-1 font-heading text-xl font-bold tracking-tight text-foreground">QR codes & invites</h2>
        <p className="mb-4 text-sm text-muted-foreground">Invite contractors to register, or print the induction QR posters for your sites.</p>
        <div className="grid gap-6 lg:grid-cols-2">
          <ContractorInvitePanel />
          <div className="rounded-md border border-border bg-white p-5 shadow-sm" data-testid="induction-qr-card">
            <h3 className="font-heading text-lg font-bold tracking-tight text-foreground">Site induction QR codes</h3>
            <p className="mt-1 mb-4 text-sm text-muted-foreground">Print and display these at each site. Contractors scan to complete their induction.</p>
            <SiteInductionQRGrid sites={sites} />
          </div>
        </div>
      </div>

      {/* Review dialog */}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="review-dialog">
          <DialogHeader><DialogTitle>Review induction — {review?.name}</DialogTitle></DialogHeader>
          {review && (
            <div className="space-y-4 text-sm">
              <p className="text-xs text-muted-foreground">You can adjust the contractor's details below before approving.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>First name</Label><Input value={edit.first_name} onChange={(e) => setE("first_name", e.target.value)} data-testid="edit-first" /></div>
                <div className="space-y-1"><Label>Last name</Label><Input value={edit.last_name} onChange={(e) => setE("last_name", e.target.value)} data-testid="edit-last" /></div>
                <div className="space-y-1"><Label>Company</Label><Input value={edit.company} onChange={(e) => setE("company", e.target.value)} data-testid="edit-company" /></div>
                <div className="space-y-1"><Label>Email</Label><Input type="email" value={edit.email} onChange={(e) => setE("email", e.target.value)} data-testid="edit-email" /></div>
                <div className="space-y-1"><Label>DBS number</Label><Input value={edit.dbs_number} onChange={(e) => setE("dbs_number", e.target.value)} data-testid="edit-dbs" /></div>
                <div className="space-y-1"><Label>Mobile</Label><Input value={edit.mobile} onChange={(e) => setE("mobile", e.target.value)} data-testid="edit-mobile" /></div>
                <div className="space-y-1"><Label>Emergency contact</Label><Input value={edit.emergency_contact} onChange={(e) => setE("emergency_contact", e.target.value)} data-testid="edit-emergency" /></div>
                <div className="space-y-1"><Label>Site</Label>
                  <Select value={edit.site} onValueChange={(v) => setE("site", v)}>
                    <SelectTrigger data-testid="edit-site"><SelectValue /></SelectTrigger>
                    <SelectContent>{sites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Notes</Label><Input value={edit.notes} onChange={(e) => setE("notes", e.target.value)} data-testid="edit-notes" /></div>
              </div>
              <div className="rounded-md border border-border p-3">
                <Badge className={review.video_confirmed ? "bg-green-600 text-white" : "bg-amber-500 text-white"}>
                  {review.video_confirmed ? "Inductee confirmed watching the site video" : "Video not confirmed"}
                </Badge>
              </div>
              <div className="rounded-md border border-brand/40 bg-brand/5 p-3">
                <div className="mb-2 text-sm font-semibold text-brand">Parts 2 &amp; 3 — Site team checklist (complete before approving)</div>
                <SchemaForm schema={SITE_INFO} data={siteData} onChange={(k, v) => setSiteData((d) => ({ ...d, [k]: v }))} />
                <div className="mt-3 space-y-2 border-t border-brand/20 pt-3">
                  <div className="space-y-1.5">
                    <Label>Inductor name</Label>
                    <Input value={inductorName} onChange={(e) => setInductorName(e.target.value)} data-testid="inductor-name-input" />
                  </div>
                  <SignaturePad label="Inductor signature" testid="inductor-signature" value={inductorSig} onChange={setInductorSig} />
                </div>
              </div>
              <div className="rounded-md border border-border p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Inductee submitted</div>
                <SchemaPrint schema={SCHEMAS.site_induction} data={review.induction_data || {}} />
              </div>
              {review.inductee_signature && (
                <div><div className="mb-1 text-xs text-muted-foreground">Contractor signature</div>
                  <img src={review.inductee_signature} alt="signature" className="h-16 rounded border border-border" /></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => reject(review)} data-testid="review-reject-btn"><X className="mr-1 h-4 w-4" /> Reject</Button>
            <Button className="bg-brand hover:bg-brand-dark" onClick={approve} disabled={busy} data-testid="review-approve-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<UserCheck className="mr-1 h-4 w-4" /> Approve & create contractor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

